import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import PrevArrow from 'components/controls/Slider/NavigationArrow/PrevArrow';
import NextArrow from 'components/controls/Slider/NavigationArrow/RightArrow';
import Styles from './AutoPlayCarousel.module.scss';

interface AutoPlayCarouselProps {
  children: React.ReactNode;
  slideToShow: number;
  sliderSetting?: any;
  autoPlayInterval?: number; // in milliseconds
}

const AutoPlayCarousel: FunctionComponent<AutoPlayCarouselProps> = (props) => {
  const { children, slideToShow, sliderSetting, autoPlayInterval = 30000 } = props;
  const sliderRef = useRef<Slider>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  const childrenArray = React.Children.toArray(children);
  const totalSlides = childrenArray.length;

  // Reset progress and start new interval
  const startAutoPlay = () => {
    // Clear existing intervals
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    // Reset progress
    setProgress(0);
    startTimeRef.current = Date.now();

    // Update progress smoothly (60 times per second for smooth animation)
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const newProgress = Math.min((elapsed / autoPlayInterval) * 100, 100);
      setProgress(newProgress);
    }, 16); // ~60fps

    // Auto-advance to next slide after interval
    intervalRef.current = setTimeout(() => {
      if (sliderRef.current) {
        const nextSlide = (currentSlide + 1) % totalSlides;
        sliderRef.current.slickGoTo(nextSlide);
      }
    }, autoPlayInterval);
  };

  // Handle slide change
  const handleAfterChange = (index: number) => {
    setCurrentSlide(index);
    startAutoPlay();
  };

  // Initialize auto-play
  useEffect(() => {
    startAutoPlay();

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide]);

  // Handle dot click
  const handleDotClick = (index: number) => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(index);
    }
  };

  return (
    <div className={Styles.carouselContainer}>
      <Slider
        ref={sliderRef}
        dots={false}
        infinite={true}
        speed={500}
        slidesToShow={slideToShow}
        slidesToScroll={slideToShow}
        prevArrow={<PrevArrow />}
        nextArrow={<NextArrow />}
        afterChange={handleAfterChange}
        responsive={sliderSetting}
        autoplay={false} // We handle auto-play manually
      >
        {children}
      </Slider>
      
      {/* Progress Bar */}
      <div className={Styles.progressBarContainer}>
        <div 
          className={Styles.progressBar}
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Dots */}
      <div className={Styles.dotsContainer}>
        {childrenArray.map((_, index) => (
          <button
            key={index}
            className={`${Styles.dot} ${index === currentSlide ? Styles.active : ''}`}
            onClick={() => handleDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
            type="button"
          />
        ))}
      </div>
    </div>
  );
};

export default AutoPlayCarousel;

